import { MODE_CONFIG } from './constants';

export const normalizeMode = (value) => {
  const raw = String(value || '').trim().toUpperCase();
  const condensed = raw.replace(/[^A-Z0-9]/g, '');
  if (raw === 'HP' || raw === 'HARDPOINT' || condensed === 'HARDPOINT') return 'HP';
  if (
    raw === 'SND' ||
    raw === 'SEARCH' ||
    raw === 'SEARCH & DESTROY' ||
    raw === 'SEARCH AND DESTROY' ||
    condensed === 'SEARCHDESTROY' ||
    condensed === 'SEARCHANDDESTROY'
  ) {
    return 'SND';
  }
  if (raw === 'CTL' || raw === 'CONTROL' || condensed === 'CONTROL') return 'CTL';
  return '';
};

const aggregateModeAverages = (rows = []) => {
  if (!rows.length) return null;

  const totals = rows.reduce(
    (acc, row) => ({
      k: acc.k + Number(row?.K || row?.kills || 0),
      d: acc.d + Number(row?.D || row?.deaths || 0),
      a: acc.a + Number(row?.A || row?.assists || 0),
      score: acc.score + Number(row?.Score || row?.score || 0),
      games: acc.games + 1,
    }),
    { k: 0, d: 0, a: 0, score: 0, games: 0 }
  );

  const avgKD = totals.d > 0 ? totals.k / totals.d : totals.k;
  return {
    games: totals.games,
    avgKD: avgKD.toFixed(2),
    avgK: (totals.k / totals.games).toFixed(1),
    avgD: (totals.d / totals.games).toFixed(1),
    avgA: (totals.a / totals.games).toFixed(1),
    avgScore: Math.round(totals.score / totals.games),
  };
};

export const buildModeCardsFromRows = (rows = []) => {
  const grouped = rows.reduce(
    (acc, row) => {
      const key = normalizeMode(row?.Mode || row?.mode);
      if (!key) return acc;
      acc[key].push(row);
      return acc;
    },
    { HP: [], SND: [], CTL: [] }
  );

  return MODE_CONFIG.map((mode) => {
    const stats = aggregateModeAverages(grouped[mode.key]);
    return stats ? { key: mode.key, label: mode.label, stats } : null;
  }).filter(Boolean);
};

const parseFirestoreTimestampToMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return (value.seconds * 1000) + Math.floor(nanos / 1e6);
  }
  return 0;
};

const extractTimeFromId = (value) => {
  const source = String(value || '');
  const match = source.match(/(\d{10,13})$/);
  if (!match) return 0;
  const raw = Number(match[1]);
  if (!Number.isFinite(raw)) return 0;
  return raw > 1e12 ? raw : raw * 1000;
};

const getRowCreatedMillis = (row = {}) =>
  parseFirestoreTimestampToMillis(row?.createdAt) ||
  parseFirestoreTimestampToMillis(row?.CreatedAt) ||
  parseFirestoreTimestampToMillis(row?.updatedAt) ||
  parseFirestoreTimestampToMillis(row?.UpdatedAt) ||
  parseFirestoreTimestampToMillis(row?.entryCreatedAt) ||
  parseFirestoreTimestampToMillis(row?.matchCreatedAt) ||
  parseFirestoreTimestampToMillis(row?.timestamp) ||
  parseFirestoreTimestampToMillis(row?.Timestamp) ||
  extractTimeFromId(row?.ModeID) ||
  extractTimeFromId(row?.matchId) ||
  extractTimeFromId(row?.id);

export const sortByDateDesc = (matches = []) =>
  [...matches].sort((a, b) => {
    const aDate = new Date(a?.Date || a?.date || 0).getTime() || 0;
    const bDate = new Date(b?.Date || b?.date || 0).getTime() || 0;
    if (bDate !== aDate) return bDate - aDate;
    return getRowCreatedMillis(b) - getRowCreatedMillis(a);
  });

export const mapModeLabel = (value) => {
  const normalized = normalizeMode(value);
  if (normalized === 'HP') return 'Hardpoint';
  if (normalized === 'SND') return 'Search & Destroy';
  if (normalized === 'CTL') return 'Control';
  return value || '';
};

export const normalizeText = (value) => String(value || '').trim().toUpperCase();
const getRowPlayerName = (row) => row?.Player || row?.IGN || row?.playerName || row?.player || '';
export const matchesSelectedPlayer = (row, selectedName) =>
  normalizeText(getRowPlayerName(row)) === normalizeText(selectedName);

